// ExecStaff.js — "บุคลากร" (pos 90/99)
//
// คำถามของหน้านี้: ครูพอไหม ใครลาสะสมเยอะ และภาระสอนกระจายเป็นธรรมไหม
//
// หน้านี้มีรายชื่อครูเป็นรายบุคคล จึงจงใจไม่มีปุ่มพิมพ์หรือ export — ตัวเลขพวกนี้
// มีไว้ให้ผู้บริหารตั้งคำถามกับหัวหน้างาน ไม่ใช่ทำรายการแจก
import React, { useMemo } from "react";
import { Badge, Col, ProgressBar, Row, Table } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import {
  ExecPage, Panel, SectionTitle, StatCard, EmptyState, ScrollTable,
  YearPicker, useExecYear, useExecData,
  fmtNum, toNum, fmtThaiDate,
} from "./ExecShell";

const SIDE_LABELS = {
  academic: "ภาคสามัญ",
  islamic: "ภาคศาสนา",
  both: "สอนทั้งสองภาค",
  ap: "อนุบาล-ประถม",
  officer: "สายสนับสนุน",
};

const EMPLOYMENT_LABELS = {
  full_time: "ประจำ",
  contract: "สัญญาจ้าง",
  part_time: "พาร์ตไทม์",
  temporary: "ชั่วคราว",
  unknown: "ยังไม่ระบุ",
};

const LEAVE_COLORS = ["#dc3545", "#fd7e14", "#d63384", "#198754", "#6f42c1", "#6c757d"];
const shortClass = (name) => String(name || "").replace("ชั้นมัธยมศึกษาปีที่ ", "ม.");

export default function ExecStaff() {
  const { years, yearId, setYearId } = useExecYear();
  const query = useMemo(() => ({ year_id: yearId }), [yearId]);
  const { data, loading, error } = useExecData("/api/executive/stats/staff", query);

  const headcount = data?.headcount || {};
  const ratio = Array.isArray(data?.ratio) ? data.ratio : [];
  const leave = useMemo(() => (data?.leave || { available: false, byType: [], byMonth: [] }), [data]);
  const topLeave = Array.isArray(data?.topLeave) ? data.topLeave : [];
  const pending = data?.pending || { available: false };
  const training = data?.training || { available: false };
  const load = useMemo(() => (data?.load || { buckets: [], top: [] }), [data]);
  const usage = data?.usage || { available: false };

  const pendingTotal = toNum(pending.leave?.total) + toNum(pending.offsite?.total) + toNum(pending.training?.total);
  const pendingStale = toNum(pending.leave?.stale) + toNum(pending.offsite?.stale) + toNum(pending.training?.stale);

  const leaveDonutOptions = useMemo(() => ({
    chart: { type: "donut" },
    labels: leave.byType.map((r) => r.lt_name),
    colors: leave.byType.map((_, i) => LEAVE_COLORS[i % LEAVE_COLORS.length]),
    legend: { position: "bottom", fontSize: "11px" },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: { show: true, total: { show: true, label: "วันลารวม", formatter: () => fmtNum(leave.totalDays) } },
        },
      },
    },
    stroke: { width: 2 },
  }), [leave]);

  const loadOptions = useMemo(() => ({
    chart: { type: "bar", toolbar: { show: false } },
    colors: ["#0d6efd"],
    plotOptions: { bar: { borderRadius: 3, columnWidth: "52%" } },
    dataLabels: { enabled: true, style: { fontSize: "10px" } },
    legend: { show: false },
    xaxis: { categories: load.buckets.map((b) => b.key), labels: { style: { fontSize: "11px" } }, title: { text: "คาบต่อสัปดาห์", style: { fontSize: "11px", fontWeight: 400 } } },
    yaxis: { labels: { style: { colors: "#6e7985", fontSize: "11px" } }, title: { text: "จำนวนครู", style: { fontSize: "11px", fontWeight: 400 } } },
    grid: { borderColor: "rgba(72,94,144,0.08)" },
  }), [load]);

  const leaveMonthOptions = useMemo(() => ({
    chart: { type: "area", toolbar: { show: false } },
    colors: ["#fd7e14"],
    stroke: { curve: "smooth", width: 2 },
    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.05 } },
    dataLabels: { enabled: false },
    xaxis: { categories: leave.byMonth.map((m) => m.month), labels: { style: { fontSize: "10px" } } },
    yaxis: { labels: { style: { colors: "#6e7985", fontSize: "11px" } } },
    grid: { borderColor: "rgba(72,94,144,0.08)" },
    tooltip: { y: { formatter: (v) => `${v} วัน` } },
  }), [leave]);

  return (
    <ExecPage
      title="บุคลากร"
      icon="ri-team-line"
      lead="จำนวนบุคลากร การลาสะสม ภาระสอน และการใช้ระบบ"
      activeYear={data?.year}
      loading={loading}
      error={error}
      toolbar={<YearPicker years={years} yearId={yearId} onChange={setYearId} activeYear={data?.year} />}
    >
      <Row className="g-3 mb-4">
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-user-3-fill" color="primary" label="บุคลากรทั้งหมด"
            value={fmtNum(headcount.total)}
            sub={`ชาย ${fmtNum(headcount.male)} · หญิง ${fmtNum(headcount.female)}`}
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-calendar-close-fill"
            color={toNum(leave.totalDays) > 0 ? "warning" : "secondary"}
            label="วันลาสะสมปีนี้"
            value={leave.available ? fmtNum(leave.totalDays) : "—"}
            sub={leave.available ? `จาก ${fmtNum(leave.byType.reduce((s, r) => s + toNum(r.requests), 0))} ใบลา` : "ยังไม่มีข้อมูลการลา"}
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-inbox-unarchive-fill"
            color={pendingStale > 0 ? "danger" : pendingTotal > 0 ? "warning" : "success"}
            label="คำขอรออนุมัติ"
            value={fmtNum(pendingTotal)}
            sub={pendingStale > 0 ? `ค้างเกิน 3 วัน ${fmtNum(pendingStale)} เรื่อง` : "ไม่มีเรื่องค้างนาน"}
            tone={pendingStale > 0 ? "danger" : undefined}
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-presentation-fill" color="info" label="ไปอบรม/ประชุม"
            value={training.available ? fmtNum(training.teachers) : "—"}
            sub={training.available
              ? `${fmtNum(training.total)} ครั้ง · ยังไม่เคยไปเลย ${fmtNum(training.neverTrained)} คน`
              : "ยังไม่มีข้อมูลการอบรม"}
          />
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col xl={4}>
          <Panel title="แยกตามภาคที่สอน">
            {(headcount.bySide || []).length === 0 ? <EmptyState compact /> : (
              <>
                {headcount.bySide.map((row) => (
                  <div key={row.side} className="mb-3">
                    <div className="d-flex justify-content-between fs-sm mb-1">
                      <span>{SIDE_LABELS[row.side] || row.side}</span>
                      <span className="fw-semibold">{fmtNum(row.total)} คน</span>
                    </div>
                    <ProgressBar
                      style={{ height: 7 }}
                      now={toNum(headcount.total) > 0 ? (toNum(row.total) / toNum(headcount.total)) * 100 : 0}
                      variant="primary"
                    />
                  </div>
                ))}
                {(headcount.byEmployment || []).length > 0 && (
                  <div className="border-top pt-3 mt-1">
                    <div className="fs-xs text-secondary mb-2">ประเภทการจ้าง</div>
                    {headcount.byEmployment.map((row) => (
                      <div key={row.employment_type} className="d-flex justify-content-between fs-sm py-1">
                        <span>{EMPLOYMENT_LABELS[row.employment_type] || row.employment_type}</span>
                        <span className="fw-semibold">{fmtNum(row.total)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </Panel>
        </Col>
        <Col xl={4}>
          <Panel title="การลาแยกตามประเภท" note="ทั้งปีปฏิทิน">
            {!leave.available || leave.byType.length === 0 ? (
              <EmptyState message="ยังไม่มีการลาในปีนี้" compact />
            ) : (
              <>
                <ReactApexChart options={leaveDonutOptions} series={leave.byType.map((r) => toNum(r.days))} type="donut" height={230} />
                <ScrollTable>
                  <Table className="table-sm mb-0 align-middle fs-xs">
                    <thead>
                      <tr><th>ประเภท</th><th className="text-end">วัน</th><th className="text-end">คน</th><th className="text-end">โควตา</th></tr>
                    </thead>
                    <tbody>
                      {leave.byType.map((row) => (
                        <tr key={row.lt_id}>
                          <td>{row.lt_name}</td>
                          <td className="text-end fw-semibold">{fmtNum(row.days)}</td>
                          <td className="text-end">{fmtNum(row.teachers)}</td>
                          <td className="text-end text-secondary">{row.quota === null ? "ไม่จำกัด" : fmtNum(row.quota)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </ScrollTable>
              </>
            )}
          </Panel>
        </Col>
        <Col xl={4}>
          <Panel title="ครูที่ลาสะสมมากที่สุด" note="เฉพาะการลาที่อนุมัติแล้ว">
            {topLeave.length === 0 ? <EmptyState compact /> : topLeave.map((row) => (
              <div key={row.t_id} className="d-flex align-items-center justify-content-between border-bottom py-2 fs-sm">
                <span className="text-truncate">
                  {row.t_name}
                  <span className="text-secondary fs-xs d-block">{row.types}</span>
                </span>
                <span className="fw-semibold flex-shrink-0">{fmtNum(row.days)} วัน</span>
              </div>
            ))}
          </Panel>
        </Col>
      </Row>

      <SectionTitle icon="ri-scales-3-line" title="ภาระสอน" note="นับคาบต่อสัปดาห์จากตารางสอนจริง" color="info" />
      <Row className="g-3 mb-4">
        <Col xl={5}>
          <Panel title="การกระจายภาระสอน" note={`ครูที่มีคาบสอน ${fmtNum(load.teachers)} คน เฉลี่ย ${fmtNum(load.avgPeriods)} คาบต่อสัปดาห์`}>
            {(load.buckets || []).length === 0 ? <EmptyState compact /> : (
              <ReactApexChart
                options={loadOptions}
                series={[{ name: "ครู", data: load.buckets.map((b) => toNum(b.total)) }]}
                type="bar" height={280}
              />
            )}
            {toNum(load.withoutTimetable) > 0 && (
              <div className="border-top pt-3 mt-3 fs-sm d-flex justify-content-between">
                <span className="text-secondary">ได้รับมอบหมายวิชาแต่ยังไม่มีคาบในตาราง</span>
                <span className="fw-semibold text-warning">{fmtNum(load.withoutTimetable)} คน</span>
              </div>
            )}
          </Panel>
        </Col>
        <Col xl={3}>
          <Panel title="ครูที่สอนมากที่สุด">
            {(load.top || []).length === 0 ? <EmptyState compact /> : load.top.map((t) => (
              <div key={t.t_id} className="d-flex align-items-center justify-content-between border-bottom py-2 fs-sm">
                <span className="text-truncate">{t.t_name}</span>
                <span className="fw-semibold flex-shrink-0">{fmtNum(t.periods)} คาบ</span>
              </div>
            ))}
          </Panel>
        </Col>
        <Col xl={4}>
          <Panel title="อัตราส่วนครูต่อนักเรียน" note="นับครูที่มีคาบสอนในชั้นนั้น">
            {ratio.length === 0 ? <EmptyState compact /> : (
              <ScrollTable>
                <Table className="table-sm mb-0 align-middle">
                  <thead>
                    <tr><th>ชั้น</th><th className="text-end">นักเรียน</th><th className="text-end">ครู</th><th className="text-end">อัตรา</th></tr>
                  </thead>
                  <tbody>
                    {ratio.map((row) => (
                      <tr key={row.c_id}>
                        <td>{shortClass(row.c_class)}</td>
                        <td className="text-end">{fmtNum(row.students)}</td>
                        <td className="text-end">{fmtNum(row.teachers)}</td>
                        <td className="text-end fw-semibold">1 : {row.ratio ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ScrollTable>
            )}
          </Panel>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col xl={7}>
          <Panel title="วันลารายเดือน" note="เห็นช่วงที่ครูลาพร้อมกันมาก">
            {!leave.available || leave.byMonth.length === 0 ? <EmptyState compact /> : (
              <ReactApexChart
                options={leaveMonthOptions}
                series={[{ name: "วันลา", data: leave.byMonth.map((m) => toNum(m.days)) }]}
                type="area" height={260}
              />
            )}
          </Panel>
        </Col>
        <Col xl={5}>
          <Panel
            title="การเข้าใช้ระบบ"
            note={usage.available ? `ย้อนหลัง ${fmtNum(usage.windowDays)} วัน` : ""}
          >
            {!usage.available ? <EmptyState message="ยังไม่มีข้อมูลการเข้าใช้ระบบ" compact /> : (
              <>
                <Row className="g-2 mb-3">
                  <Col xs={4}>
                    <div className="border rounded p-2 text-center">
                      <div className="fs-5 fw-semibold text-success">{fmtNum(usage.active)}</div>
                      <div className="fs-xs text-secondary">เข้าใช้อยู่</div>
                    </div>
                  </Col>
                  <Col xs={4}>
                    <div className="border rounded p-2 text-center">
                      <div className="fs-5 fw-semibold text-warning">{fmtNum(usage.inactive)}</div>
                      <div className="fs-xs text-secondary">ไม่ได้เข้านาน</div>
                    </div>
                  </Col>
                  <Col xs={4}>
                    <div className="border rounded p-2 text-center">
                      <div className="fs-5 fw-semibold text-secondary">{fmtNum(usage.neverLoggedIn)}</div>
                      <div className="fs-xs text-secondary">ไม่เคยเข้าเลย</div>
                    </div>
                  </Col>
                </Row>
                {(usage.inactiveList || []).length > 0 && (
                  <>
                    <div className="fs-xs text-secondary mb-2">ไม่ได้เข้าระบบนานที่สุด</div>
                    {usage.inactiveList.slice(0, 8).map((row) => (
                      <div key={row.t_id} className="d-flex justify-content-between border-bottom py-2 fs-sm">
                        <span className="text-truncate">{row.t_name}</span>
                        <span className="text-secondary fs-xs flex-shrink-0">ล่าสุด {fmtThaiDate(row.last_login)}</span>
                      </div>
                    ))}
                  </>
                )}
                <div className="fs-xs text-secondary mt-3">
                  ครูที่ไม่ได้เข้าระบบมักอธิบายได้ว่าทำไมห้องนั้นไม่มีการเช็คชื่อหรือไม่มีการบันทึกคะแนน
                </div>
              </>
            )}
          </Panel>
        </Col>
      </Row>

      {pending.available && pendingTotal > 0 && (
        <Row className="g-3 mb-4">
          <Col xs={12}>
            <Panel title="คำขอที่รออนุมัติ" note="หัวหน้าบุคลากรเป็นผู้อนุมัติ">
              <Row className="g-3">
                {[
                  { key: "leave", label: "ใบลา", icon: "ri-user-unfollow-line", row: pending.leave },
                  { key: "offsite", label: "ออกนอกสถานที่", icon: "ri-walk-line", row: pending.offsite },
                  { key: "training", label: "อบรม/ประชุม", icon: "ri-presentation-line", row: pending.training },
                ].map(({ key, label, icon, row }) => (
                  <Col md={4} key={key}>
                    <div className={`border rounded p-3 ${toNum(row?.stale) > 0 ? "border-danger" : ""}`}>
                      <div className="d-flex align-items-center justify-content-between">
                        <span><i className={`${icon} me-1 text-secondary`} />{label}</span>
                        <span className="fs-4 fw-semibold">{fmtNum(row?.total)}</span>
                      </div>
                      {toNum(row?.stale) > 0 && (
                        <Badge bg="danger" className="mt-2">ค้างเกิน 3 วัน {fmtNum(row.stale)} เรื่อง</Badge>
                      )}
                    </div>
                  </Col>
                ))}
              </Row>
            </Panel>
          </Col>
        </Row>
      )}
    </ExecPage>
  );
}
